/*
 * Copyright © 2025 Gavin Sawyer. All rights reserved.
 */

import { inject, Injectable, signal, type Signal, type WritableSignal }              from "@angular/core";
import { toObservable, toSignal }                                                    from "@angular/core/rxjs-interop";
import type * as configLib                                                           from "@bowstring/config";
import type * as currenciesLib                                                       from "@bowstring/currencies";
import { CONFIG }                                                                    from "@bowstring/injection-tokens";
import { type Currency }                                                             from "@bowstring/types";
import { combineLatestWith, filter, map, type Observable, of, startWith, switchMap } from "rxjs";
import { webSocket, type WebSocketSubject }                                          from "rxjs/webSocket";
import { RxSsrService }                                                              from "./RxSsrService";


interface ExchangeRateData {
  "price": string;
  "product_id": `BTC-${ Currency }`;
  "type": "ticker";
}

interface ExchangeRateSubscription {
  "channels": Array<{
    "name": "ticker";
    "product_ids": Array<`BTC-${ Currency }`>;
  }>;
  "type": "subscribe";
}

interface ExchangeRateUnsubscription {
  "channels": [ "ticker" ];
  "product_ids": Array<`BTC-${ Currency }`>;
  "type": "unsubscribe";
}

@Injectable({ providedIn: "root" })
export class ExchangeService {

  private readonly configLib: typeof configLib = inject<typeof configLib>(CONFIG);
  private readonly rxSsrService: RxSsrService  = inject<RxSsrService>(RxSsrService);

  public readonly userCurrency$: WritableSignal<keyof typeof currenciesLib> = signal<keyof typeof currenciesLib>(this.configLib.shop.currency);
  public readonly rate$: Signal<number | undefined>                         = toSignal<number | undefined>(
    toObservable<keyof typeof currenciesLib>(this.userCurrency$).pipe<number | undefined>(
      this.rxSsrService.wrap<keyof typeof currenciesLib, number | undefined>(
        switchMap<keyof typeof currenciesLib, Observable<number | undefined>>(
          (userCurrency: keyof typeof currenciesLib): Observable<number | undefined> => {
            const shopCurrency: Currency = this.configLib.shop.currency;

            if (userCurrency !== shopCurrency) {
              const webSocketSubject: WebSocketSubject<ExchangeRateData> = webSocket<ExchangeRateData>("wss://ws-feed.exchange.coinbase.com");

              return webSocketSubject.multiplex(
                (): ExchangeRateSubscription => ({
                  channels: [
                    {
                      name:        "ticker",
                      product_ids: [ `BTC-${ shopCurrency }` ],
                    },
                  ],
                  type:     "subscribe",
                }),
                (): ExchangeRateUnsubscription => ({
                  channels:    [ "ticker" ],
                  product_ids: [ `BTC-${ shopCurrency }` ],
                  type:        "unsubscribe",
                }),
                (exchangeRateData: ExchangeRateData): exchangeRateData is ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` } => exchangeRateData.type === "ticker" && exchangeRateData.product_id === `BTC-${ shopCurrency }`,
              ).pipe<[ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null | undefined ], [ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null ], number, number | undefined>(
                combineLatestWith<ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, [ ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null | undefined ]>(
                  userCurrency === "BTC" ? of<null>(null) : webSocketSubject.multiplex(
                    (): ExchangeRateSubscription => ({
                      channels: [
                        {
                          name:        "ticker",
                          product_ids: [ `BTC-${ userCurrency }` ],
                        },
                      ],
                      type:     "subscribe",
                    }),
                    (): ExchangeRateUnsubscription => ({
                      channels:    [ "ticker" ],
                      product_ids: [ `BTC-${ userCurrency }` ],
                      type:        "unsubscribe",
                    }),
                    (exchangeRateData: ExchangeRateData): exchangeRateData is ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } => exchangeRateData.type === "ticker" && exchangeRateData.product_id === `BTC-${ userCurrency }`,
                  ).pipe<ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | undefined>(
                    startWith<ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` }, [ undefined ]>(undefined),
                  ),
                ),
                filter<[ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null | undefined ], [ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null ]>(
                  (exchangeRateData: [ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null | undefined ]): exchangeRateData is [ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null ] => !!exchangeRateData[1] || exchangeRateData[1] === null,
                ),
                map<[ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null ], number>(
                  ([ { price }, exchangeRateDataUser ]: [ ExchangeRateData & { "product_id": `BTC-${ typeof shopCurrency }` }, ExchangeRateData & { "product_id": `BTC-${ typeof userCurrency }` } | null ]): number => parseInt(exchangeRateDataUser?.price || "1") / parseInt(price),
                ),
                startWith<number, [ undefined ]>(undefined),
              );
            }

            return of<1>(1);
          },
        ),
        "01967ded-2dbf-7658-abc9-91369a00969d",
      ),
    ),
  );

}
